import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from 'src/components/controls/forms/redesign/FormButton';
import ReportFilterLocation from './ReportFilterLocation';
import useFetch from 'use-http';
import { HorizonInterval } from 'src/models/Horizon';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import Tag from 'src/models/Tag';
import { useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import ReportHorizonSequencing from 'src/models/ReportHorizonSequencing';
import ReportName from 'src/models/ReportName';
import FilteredReport from 'src/models/FilteredReport';
import ReportTownshipDto from 'src/models/reports/ReportTownshipDto';
import ReportHorizonDto from 'src/models/reports/ReportHorizonDto';
import ReportDataTree from 'src/models/reports/ReportDataTree';
import LoadingSpinner from 'src/components/common/LoadingSpinner';

interface IReportFilterProps {
    reportNumber: number;
    reportName: ReportName;
    horizonIntervals: HorizonInterval[];
    // selectedHorizons: HorizonInterval[];
    selectedTags: Tag[];
    selectedSequence: ReportHorizonSequencing;
    loadingReport: boolean;
    isQueued: boolean;
    updateHorizonIntervals: (horizons: ReportHorizonDto[]) => void;
    onGetReportId: (reportId: number) => void;
    setLoadingReport: (loading: boolean) => void;
}

const ReportFilter = (props: IReportFilterProps) => {
    const appContext = useContext<IAppContextState>(AppContext);

    const { project } = useSelector((state: IRootState) => state);
    const { getValues, setValue, register } = useForm();
    const { get, post, response } = useFetch();

    const [tagFilteredSelections, setTagFilteredSelections] = useState<string[]>([]);
    const [townships, setTownships] = useState<ReportTownshipDto[]>([]);
    const [selectedHorizons, setSelectedHorizons] = useState<ReportDataTree[]>([]);
    // Set to prevent triple-nested for-loop logic.
    const [flatHorizonList, setFlatHorizonList] = useState<ReportHorizonDto[]>([]);
    const [filteredList, setFilteredList] = useState<ReportDataTree[]>([]);
    const [loadingFields, setLoadingFields] = useState<boolean>(true);
    
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [expandAll, setExpandAll] = useState<boolean>(false);
    const [parentTier, setParentTier] = useState<number>(undefined);
    const [reportDisabled, setReportDisabled] = useState<boolean>(false);

    const [widthBound, setWidthBound] = useState<number>(500);
    // const watchAllProject = watch('allProject', 'true');

    useEffect(() => {
        if (!!project.currentProject) {
            getTownships();
        }
    }, [project]);

    useEffect(() => {
        if (props.selectedTags?.length > 0) {
            getTagFilteredItems();
        } else {
            updateFilteredTagSelections([], true);
        }
    }, [props.selectedTags]);

    useEffect(() => {
        if (props.isQueued || props.loadingReport || (props.reportName.sequencingRequired === true && !props.selectedSequence)) {
            setReportDisabled(true);
        } else {
            setReportDisabled(false);
        }
    }, [props.loadingReport, props.selectedSequence, props.reportName]);

    useEffect(() => {
        updateFilteredTagSelections(tagFilteredSelections, false);
        if (townships?.length > 0) {
            let hList: ReportHorizonDto[] = [];

            // TODO: There's almost certainly a some Javascript function that can handle this.
            // Flatten is what I was seeing, but it looks like our version doesn't have that.
            for (let t of townships ?? []) {
                for (let fl of t.fieldList ?? []) {
                    for (let tr of fl.tractList ?? []) {
                        hList = hList.concat(tr.horizonList);
                    }
                }
            }

            setFlatHorizonList(hList);

            let bounds = document.getElementById('report-filter').getBoundingClientRect();
            setWidthBound(bounds.width);
        }
    }, [townships]);

    useEffect(() => {
        processHorizonIntervals();
    }, [selectedHorizons]);

    const getTownships = async () => {
        const result = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/report-townships/${project.currentProject.landSystemId}`);

        if (response.ok) {
            setTownships(result);
        } else {
            console.log('Error getting Townships', result);
            window.alert('Unable to retrieve Townships for Report!');
            setTownships([]);
            setLoadingFields(false);
        }
    };

    const getTagFilteredItems = async () => {
        let tagString = props.selectedTags.map((st) => st.name).join(',');

        let newTagGlobals = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/tag-list/${tagString}`);

        if (response.ok) {
            updateFilteredTagSelections(newTagGlobals, true);
        } else {
            window.alert('Unable to retrieve Global Ids from Tags.');
        }
    };

    const runReport = async () => {
        let sequenceBody = new FilteredReport;
        if (!!props.selectedSequence) {
            sequenceBody.horizonSequences = props.selectedSequence.horizonSequences;
        } else if (props.reportName.sequencingRequired === true) {
            window.alert('Horizon Sequencing required!');
            return;
        }
        props.setLoadingReport(true);

        // Make sure this will still work when sequencing is not required.
        let idList = getGlobalFromCheckList();
        sequenceBody.filters = idList.join(',');        
        
        if (idList.length > 0) {
            let newReportId = await post(`v1.0/reports/${props.reportName.id}/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/by-filter`, sequenceBody);
            if (response.ok) {  
                props.onGetReportId(newReportId);
            } else {
                window.alert('Report Generation Failed. Please Contact System Admin');
            }
        }
    };

    const getGlobalFromCheckList = () => {
        let globals: string[] = [];
        let tagFilters = [...tagFilteredSelections];
        tagFilters = tagFilters.map((tg) => tg.split(',').join('.'));
        let fullList = flattenItems(filteredList, true);
        
        for (let item of fullList) {
            let keyGlobal = item.reportGlobal.split(',').join('.');

            if (item.selected && !tagFilters.includes(keyGlobal)) {
                globals.push(keyGlobal);
            } else if (!item.selected && tagFilters.includes(keyGlobal)) {
                tagFilters.splice(tagFilters.indexOf(item.reportGlobal), 1);
            }
        }

        globals = globals.concat(tagFilters);

        return globals;
    };

    // Need to grab the Township based on the Section, as Township is not connected to a global id.
    const updateFilteredTagSelections = (newGlobalIds: string[], refreshTagList: boolean) => {
        let globalKeys = newGlobalIds.map(ng => ng.split('.').join(','));
        let childKeys: string[] = [];
        let removeKeys: string[] = [];
        let flatList = flattenItems(filteredList, true);
        let currentKeys = flatList.map(fl => fl.reportGlobal.split('.').join(','));
        
        for (let global of globalKeys) {
            let node = flatList.find(fl => fl.reportGlobal === global);
            if (!!node) {
                handleSelect(node, true, true);
            }
            let children = currentKeys.filter(ck => ck.substring(0, global.length) === global);
            childKeys = childKeys.concat(children);
        }

        // TODO: Work on consolidating all of this logic so we don't have to do these transforms.
        let updateSelect = [...selectedHorizons];
        let updateCheck = updateSelect.map(us => us.id);
        let rCheck: number[] = [];

        if (refreshTagList === true) {
            let prevTags = [...tagFilteredSelections];
    
            for (const tag of prevTags) {
                if (currentKeys.includes(tag) && !(globalKeys.includes(tag) || childKeys.includes(tag))) {
                    let children = currentKeys.filter(ck => ck.substring(0, tag.length) === tag);
                    removeKeys = removeKeys.concat(children);            
                    let node = flatList.find(fl => fl.reportGlobal === tag);
                    if (!!node) {
                        handleSelect(node, false, true);
                    }
                }
            }

            rCheck = selectedHorizons
                .filter(sl => removeKeys.includes(sl.reportGlobal.replace('-', ',').split('.').join(',')))
                .map(sh => sh.id);
    
            setTagFilteredSelections(globalKeys);
        }

        let addList = flatHorizonList.filter(hl => 
            childKeys.includes(hl.globalId.replace('-', ',').split('.').join(',')) &&
            !updateCheck.includes(hl.id) && !rCheck.includes(hl.id)
        );

        setSelectedHorizons(ReportDataTree.FromHorizons(addList));
    };

    const updateSelectAll = () => {
        let currentForm = getValues();
        setSelectAll(currentForm.but_select_all === true);
    };

    const updateExpandAll = () => {
        let currentForm = getValues();
        setExpandAll(currentForm.but_expand_all === true);
    };

    const processHorizonIntervals = () => {
        let selected = selectedHorizons.map(sh => sh.id);
        let hSummaries = flatHorizonList.filter(h => selected.includes(h.id));
        props.updateHorizonIntervals(hSummaries);
    };

    const flattenItems = (nodes: ReportDataTree[], showChildren: boolean = false, flattenedTree: ReportDataTree[] = []) => {
        for (const node of nodes) {
            if (!flattenedTree.includes(node)) {
                flattenedTree.push(node);
            }
            if ((showChildren || node.expanded) && node.children.length > 0) {
                flattenedTree = flattenItems(node.children, showChildren, flattenedTree);
            }
        }

        return flattenedTree;
    };

    const handleSelect = (currentRow: ReportDataTree, isSelected: boolean, isTop: boolean = false) => {
        let flatList = [...filteredList];

        if (currentRow.children.length > 0) {
            for (let c of currentRow.children) {
                flatList = handleSelect(c, isSelected);
            }
        }

        currentRow.selected = isSelected;
        
        if (isTop) {
            currentRow.expanded = currentRow.selected;
            let newFlat = flattenItems(flatList.filter(fl => fl.reportTier === parentTier));
            setFilteredList(newFlat);
        }

        return flatList;
    };

    return (
        <div>
            {/* <RHFLegend title="Filter" /> */}
            <div style={{ fontSize: '1.75rem' }}>Filter</div>

            <div>
                <form>
                    <div>
                        <div style={{ height: '47vh', overflow: 'auto' }}>
                            <div>
                                {/* NOTE: ALL INPUT BUTTONS CONNECTED TO FORM MUST START WITH 'BUT' */}
                                <div style={{ display: 'flex', height: '5%' }}>
                                    <input 
                                        type="checkbox" 
                                        id="but_select_all" 
                                        name="but_select_all"
                                        ref={register}
                                        onChange={() => updateSelectAll()}
                                        disabled={loadingFields}
                                    />
                                    <label htmlFor="selectAll">Select All</label>

                                    <div style={{ margin: '0 1rem' }}>|</div>

                                    <input 
                                        type="checkbox" 
                                        id="but_expand_all" 
                                        name="but_expand_all"
                                        ref={register}
                                        onChange={() => updateExpandAll()}
                                        disabled={loadingFields}
                                    />
                                    <label htmlFor="selectAll">Expand All</label>
                                </div>
                            </div>
                            <div style={{ height: '95%' }} id="report-filter">
                                <ReportFilterLocation
                                    landSystemId={project.currentProject?.landSystemId} 
                                    townships={townships}
                                    // selectedHorizonIds={props.selectedHorizons.map(sh => sh.horizonId)}
                                    selectAll={selectAll}
                                    expandAll={expandAll}
                                    loadingFields={loadingFields}
                                    selectedHorizons={selectedHorizons}
                                    width={widthBound}
                                    filteredList={filteredList}
                                    parentTier={parentTier}
                                    refreshTagValues={() => updateFilteredTagSelections(tagFilteredSelections, false)}
                                    flattenItems={flattenItems}
                                    getValues={getValues}
                                    register={register}
                                    setValue={setValue}
                                    handleSelect={handleSelect}
                                    setLoadingFields={(loading: boolean) => setLoadingFields(loading)}
                                    setSelectedHorizons={(horizons: ReportDataTree[]) => setSelectedHorizons(horizons)}
                                    setFilteredList={setFilteredList}
                                    setParentTier={setParentTier}
                                />
                            </div>
                        </div>
                        <div>
                            <div style={{ display: 'flex flex-end' }}>
                                <a style={{ display: 'hidden' }} id="rep" />
                                <div>
                                    <Button text="Run Report" intent="primary" onClick={runReport} disabled={reportDisabled} />
                                </div>
                                {props.isQueued && (
                                    <div style={{alignContent: 'center'}}>
                                        <span>Report Queued successfully.</span>
                                    </div>
                                )}
                                {props.loadingReport && (
                                    <div style={{alignContent: 'center'}}>
                                        <span>Fetching Report...</span>
                                        <LoadingSpinner />
                                    </div>
                                )}
                                {!(props.loadingReport || props.isQueued) && (
                                    <div>
                                        <div style={{ display: 'flex' }}>
                                            <input type="radio" id="excel" name="mor_output_type" defaultChecked={true} />
                                            <label htmlFor="excel">Excel</label>

                                            <div style={{ margin: '0 1rem' }}>|</div>

                                            <input type="radio" id="pdf" name="mor_output_type" />
                                            <label htmlFor="pdf">PDF</label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReportFilter;



// WEBPACK FOOTER //
// ./src/components/project/sections/ReportComponents/ReportFilter.tsx